'use client';

/**
 * Общее оформление графиков.
 *
 * Собрано в одном месте, чтобы линия на дашборде и та же линия в отчёте не
 * выглядели как два разных продукта.
 *
 * Оси без своих линий и засечек: сетка уже задаёт масштаб, а рамка вокруг
 * графика съедает место и утяжеляет карточку. Цвета — через переменные
 * палитры, поэтому тёмная тема получает их бесплатно.
 */

import React from 'react';
import type { TooltipProps } from 'recharts';

/** Ряды. Различимы и на светлой теме, и на тёмной. */
export const seriesColors = ['#2563eb', '#0891b2', '#7c3aed', '#db2777', '#ea580c', '#16a34a'];

export const gridProps = {
  strokeDasharray: '3 3',
  stroke: 'rgb(var(--c-slate-200))',
  vertical: false,
} as const;

export const axisProps = {
  tick: { fontSize: 11, fill: 'rgb(var(--c-slate-400))' },
  tickLine: false,
  axisLine: false,
} as const;

/**
 * Подсказка карточкой, а не серым прямоугольником recharts: тот рисуется
 * белым в любой теме и в тёмной становится пятном.
 *
 * `null` в значении подписывается словами. Пустой день на графике — это «не
 * оценивали», и подсказка обязана сказать именно это, а не показать пустоту.
 */
export function ChartTooltip({
  active,
  payload,
  label,
  emptyLabel = 'нет данных',
  suffix = '',
}: TooltipProps<number, string> & { emptyLabel?: string; suffix?: string }) {
  if (!active || !payload?.length) return null;

  return (
    <div className="bg-white border border-slate-200 rounded-lg shadow-lg px-3 py-2 text-xs">
      <div className="font-semibold text-slate-700 mb-1.5">{label}</div>
      {payload.map((item) => (
        <div key={String(item.dataKey)} className="flex items-center gap-2 leading-relaxed">
          <span
            className="w-2 h-2 rounded-full flex-shrink-0"
            style={{ background: item.color }}
            aria-hidden
          />
          <span className="text-slate-500">{item.name}:</span>
          <span className="font-semibold text-slate-800 ml-auto tabular-nums">
            {item.value === null || item.value === undefined
              ? emptyLabel
              : `${item.value}${suffix}`}
          </span>
        </div>
      ))}
    </div>
  );
}
